/** `fallback` is the sentence for a code with no mapping of its own. */
export const getFirebaseErrorMessage = (
    errorCode: string,
    fallback = 'An unexpected error occurred. Please try again.'
): string => {
    const errorMessages: Record<string, string> = {
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/user-not-found': 'No account found with this email. Please sign up first.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
        'auth/weak-password': 'Password should be at least 8 characters long.',
        'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
        'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your internet connection.',
        'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
        'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
        'auth/popup-blocked': 'Sign-in popup was blocked by your browser. Please allow popups and try again.',
    };

    return errorMessages[errorCode] || fallback;
};

export const getBackendErrorMessage = (errorCode: string): string => {
    const errorMessages: Record<string, string> = {
        'MISSING_EMAIL': 'Please enter your email address.',
        'USER_NOT_FOUND_NO_INVITE': 'No account found. Please wait for our invite.',
        'EMAIL_NOT_VERIFIED': 'Your email address is not verified yet. Please use the verification link we sent before signing in.',
        'INVALID_AUTH_TOKEN': 'Authentication failed. Please try signing in again.',
        'USER_DISABLED': 'Your account has been disabled. Please contact support.',
        'USER_NOT_FOUND': 'Account not found.',
        'INTERNAL_ERROR': 'Something went wrong on our end. Please try again later.'
    };

    return errorMessages[errorCode] || 'An unexpected error occurred.';
};

/**
 * Firebase codes that mean an emailed password-reset link
 * can no longer be used: it expired, it was already used, or the account it
 * was for is gone. All of them get the same "This link has expired" screen.
 */
const DEAD_ACTION_LINK_CODES = new Set([
    'auth/expired-action-code',
    'auth/invalid-action-code',
    'auth/user-not-found',
]);

export const isDeadActionLink = (errorCode?: string): boolean =>
    !!errorCode && DEAD_ACTION_LINK_CODES.has(errorCode);

/**
 * A verification link the backend refused as spent or expired (410) or
 * malformed (400). Anything else — network, 5xx, 429 — says nothing about the
 * link, so the page offers "Try again" instead.
 */
export const isDeadVerificationLink = (err: any): boolean => {
    const status = err?.response?.status;
    return status === 410 || status === 400;
};

/** Copy for a failed `confirmPasswordReset` that is not a dead link. */
export const getResetPasswordErrorMessage = (errorCode?: string): string => {
    const errorMessages: Record<string, string> = {
        'auth/weak-password': 'Choose a longer password.',
        'auth/password-does-not-meet-requirements': 'Choose a longer password.',
        'auth/user-disabled': 'This account has been disabled. Contact support.',
        'auth/network-request-failed': "We couldn't reach the server. Check your connection and try again.",
        'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
    };

    return (errorCode && errorMessages[errorCode]) || "We couldn't update your password. Try again in a moment.";
};

/** Copy for a failed `POST /auth/password-resets` (HTTP status, if any). */
export const getPasswordResetRequestError = (status?: number): string =>
    status === 429
        ? 'Too many requests. Wait a few minutes and try again.'
        : "We couldn't send the link. Try again in a moment.";

/**
 * The backend did not accept the request for a sign-up verification link.
 * Carries no Firebase `code`, so callers that show `err.message` for
 * code-less errors show this sentence.
 */
export class VerificationLinkError extends Error {
    constructor() {
        super("We couldn't send the link. Try again in a moment.");
        this.name = 'VerificationLinkError';
    }
}

export const isVerificationLinkError = (err: unknown): err is VerificationLinkError =>
    (err as Error | null)?.name === 'VerificationLinkError';
